import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { puedeCertificarEscalamiento } from "./permisos";

const API = process.env.REACT_APP_API_URL;

const CLARO_RED   = "#E30613";
const CLARO_DARK  = "#0f172a";
const CLARO_MUTED = "#64748b";
const BORDER      = "#e2e8f0";
const BORDER_SOFT = "#f1f5f9";

// ── Flujo de 4 estados ──────────────────────────────────────────
// En Revisión (al crear el caso) → Escalado NOC → Nodo Certificado / Devuelto.
// El paso a "Escalado NOC" mide cuánto se demoró desde que se creó el caso;
// llegar a uno de los 2 estados finales mide cuánto se demoró desde que se
// escaló al NOC.
const ESTADOS = ["En Revision", "Resuelto", "Escalado NOC", "Nodo Certificado", "Devuelto"];
const ESTADO_INFO = {
  "En Revision":      { label: "En Revisión",     bg: "#e0f2fe", color: "#075985", dot: "#0ea5e9" },
  "Resuelto":         { label: "Resuelto",         bg: "#ede9fe", color: "#6d28d9", dot: "#8b5cf6" },
  "Escalado NOC":     { label: "Escalado NOC",     bg: "#fef9c3", color: "#854d0e", dot: "#eab308" },
  "Nodo Certificado": { label: "Nodo Certificado", bg: "#dcfce7", color: "#15803d", dot: "#22c55e" },
  "Devuelto":         { label: "Devuelto",         bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
};
const TIPOS_SOLICITUD = ["Creación TAS", "Certificación Huella"];

// ── Cada tipo de solicitud tiene su propio flujo de estados ──
// Creación TAS: la gestión corre por cuenta propia, sin pasar por el NOC —
// por eso no tiene "Escalado NOC" ni "Nodo Certificado".
// Certificación Huella: pasa por el NOC hasta quedar certificado — no usa
// "Resuelto" (ese estado es solo para gestión directa).
const ESTADOS_POR_TIPO = {
  "Creación TAS":        ["En Revision", "Resuelto", "Devuelto"],
  "Certificación Huella": ["En Revision", "Escalado NOC", "Nodo Certificado", "Devuelto"],
};
function estadosParaTipo(tipo) {
  return ESTADOS_POR_TIPO[tipo] || ESTADOS;
}

// ── Formato de tiempo transcurrido (minutos → "Xd Yh" / "Xh Ym") ──
function formatoDuracion(minutos) {
  if (minutos == null || isNaN(minutos)) return "—";
  const m = Math.max(0, Math.round(minutos));
  const dias = Math.floor(m / (60 * 24));
  const horas = Math.floor((m % (60 * 24)) / 60);
  const mins = m % 60;
  if (dias > 0) return `${dias}d ${horas}h`;
  if (horas > 0) return `${horas}h ${mins}m`;
  return `${mins}m`;
}

function formatoFecha(f) {
  if (!f) return "—";
  const s = String(f);
  const [fecha, hora] = s.split(" ");
  if (!fecha) return "—";
  const [y, m, d] = fecha.split("-");
  return `${d}/${m}/${y}${hora ? " " + hora.slice(0, 5) : ""}`;
}

const MESES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
function formatoMes(mesYYYYMM) {
  if (!mesYYYYMM) return "—";
  const [y, m] = mesYYYYMM.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MESES_CORTOS[idx] || m} ${y}`;
}

// "YYYY-MM-DD HH:MM:SS" (MySQL) ⇄ "YYYY-MM-DDTHH:MM" (input datetime-local)
function aDatetimeLocal(f) {
  if (!f) return "";
  return String(f).slice(0, 16).replace(" ", "T");
}
function ahoraDatetimeLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Tag({ text, bg, color, dot }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      padding: "3px 10px", borderRadius: "20px",
      fontSize: "11px", fontWeight: "700", color, background: bg,
    }}>
      {dot && <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: dot }} />}
      {text}
    </span>
  );
}

function KpiCard({ label, value, sub, bg, color, onClick }) {
  const display = (value == null || !isFinite(value) || isNaN(value)) ? "—" : value;
  return (
    <div onClick={onClick} style={{ background: bg, borderRadius: "12px", padding: "14px 18px", minWidth: "140px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", flex: 1, cursor: onClick ? "pointer" : "default" }}>
      <div style={{ fontSize: "22px", fontWeight: "800", color, lineHeight: 1 }}>{display}</div>
      <div style={{ fontSize: "11px", color: CLARO_MUTED, marginTop: "4px" }}>{label}</div>
      {sub && <div style={{ fontSize: "10px", color, marginTop: "2px", fontWeight: "600" }}>{sub}</div>}
    </div>
  );
}

function Th({ children }) {
  return <th style={{ padding: "10px 10px", textAlign: "left", fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: `1px solid ${BORDER}`, whiteSpace: "nowrap" }}>{children}</th>;
}
function Td({ children, style }) {
  return <td style={{ padding: "9px 10px", fontSize: "12.5px", color: CLARO_DARK, borderBottom: `1px solid ${BORDER_SOFT}`, ...style }}>{children}</td>;
}

const card = {
  background: "white", borderRadius: "14px", border: `1px solid ${BORDER}`,
  boxShadow: "0 2px 10px rgba(0,0,0,0.05)", padding: "20px",
};
const inputSt = { padding: "9px 12px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "13px", fontFamily: "inherit" };
const labelSt = { fontSize: "11px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "7px", display: "block" };

// ── Tabla dinámica estilo Excel (mismo look que Backlog Regional): encabezado
// rojo, Total general en rojo fuerte, filas desplegables con sombreado
// mientras están abiertas y cualquier celda (nombre o cifra) funciona como
// filtro de la tabla de detalle de abajo. ──
const estiloPivotTh = {
  background: "#C00000", color: "white", fontWeight: "700", padding: "10px 13px",
  textAlign: "left", whiteSpace: "nowrap", fontSize: "12px", letterSpacing: "0.015em",
  borderBottom: "2px solid #8c0000",
};
const estiloPivotThNum = { ...estiloPivotTh, textAlign: "right" };
const estiloPivotTd = {
  padding: "9px 13px", background: "white", color: "#101114", fontSize: "13px",
  fontVariantNumeric: "tabular-nums", borderBottom: `1px solid ${BORDER_SOFT}`,
};
const estiloPivotTdNum = { ...estiloPivotTd, textAlign: "right" };
function MarcoTablaPivote({ children }) {
  return (
    <div style={{ borderRadius: "12px", border: `1px solid ${BORDER}`, overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>{children}</table>
    </div>
  );
}

// ── Tabla dinámica Aliado → Ciudad → Nodo. `onFiltrar(nivel, label)` fija el
// filtro de ese nivel (limpiando los más profundos); pasar `null` lo quita.
function TablaPivoteDevoluciones({ datos, filtroAliado, filtroCiudad, filtroNodo, onFiltrar, onLimpiar }) {
  const [aliadosAbiertos, setAliadosAbiertos] = useState(new Set());
  const [ciudadesAbiertas, setCiudadesAbiertas] = useState(new Set());
  const alternar = (set, setSet, key) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const totalGeneral = datos.reduce((s, a) => s + a.total, 0);
  const devolucionesGeneral = datos.reduce((s, a) => s + a.devoluciones, 0);
  return (
    <MarcoTablaPivote>
      <colgroup><col style={{ width: "46%" }} /><col /><col /></colgroup>
      <thead>
        <tr>
          <th style={estiloPivotTh}>ALIADO / CIUDAD / NODO</th>
          <th style={estiloPivotThNum}>Casos</th>
          <th style={estiloPivotThNum}>Devoluciones</th>
        </tr>
      </thead>
      <tbody>
        {datos.map((a) => {
          const aAbierto = aliadosAbiertos.has(a.label);
          const aSeleccionado = filtroAliado === a.label && !filtroCiudad;
          const aFondo = aSeleccionado ? "#fde8e8" : aAbierto ? "#F8CBAD" : undefined;
          const aColor = aAbierto && !aSeleccionado ? "#7a2f00" : undefined;
          return (
            <Fragment key={a.label}>
              <tr style={aSeleccionado ? { outline: "2px solid #0f172a", outlineOffset: "-2px" } : undefined}>
                <td style={{ ...estiloPivotTd, cursor: "pointer", background: aFondo, color: aColor, fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
                  <span onClick={(e) => { e.stopPropagation(); alternar(aliadosAbiertos, setAliadosAbiertos, a.label); }}
                    style={{ cursor: "pointer", color: aColor || CLARO_MUTED, fontSize: "10px", userSelect: "none", width: "12px", display: "inline-block" }}>
                    {aAbierto ? "▾" : "▸"}
                  </span>
                  <span onClick={() => onFiltrar(aSeleccionado ? null : "aliado", a.label)} style={{ cursor: "pointer" }}>{a.label}</span>
                </td>
                <td style={{ ...estiloPivotTdNum, cursor: "pointer", background: aFondo, color: aColor, fontWeight: "700" }} onClick={() => onFiltrar(aSeleccionado ? null : "aliado", a.label)}>{a.total}</td>
                <td style={{ ...estiloPivotTdNum, cursor: "pointer", background: aFondo, color: aColor, fontWeight: "700" }} onClick={() => onFiltrar(aSeleccionado ? null : "aliado", a.label)}>
                  {a.devoluciones > 0 ? <Tag text={`${a.devoluciones}×`} bg="#ffedd5" color="#c2410c" /> : "—"}
                </td>
              </tr>
              {aAbierto && a.ciudades.map((c) => {
                const ciudadKey = `${a.label}||${c.label}`;
                const cAbierto = ciudadesAbiertas.has(ciudadKey);
                const cSeleccionado = filtroAliado === a.label && filtroCiudad === c.label && !filtroNodo;
                const cFondo = cSeleccionado ? "#fde8e8" : cAbierto ? "#DDEBF7" : undefined;
                return (
                  <Fragment key={ciudadKey}>
                    <tr style={cSeleccionado ? { outline: "2px solid #0f172a", outlineOffset: "-2px" } : undefined}>
                      <td style={{ ...estiloPivotTd, paddingLeft: "30px", cursor: "pointer", background: cFondo, fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                        <span onClick={(e) => { e.stopPropagation(); alternar(ciudadesAbiertas, setCiudadesAbiertas, ciudadKey); }}
                          style={{ cursor: "pointer", color: CLARO_MUTED, fontSize: "10px", userSelect: "none", width: "12px", display: "inline-block" }}>
                          {cAbierto ? "▾" : "▸"}
                        </span>
                        <span onClick={() => onFiltrar(cSeleccionado ? "aliado" : "ciudad", cSeleccionado ? a.label : c.label, a.label)} style={{ cursor: "pointer" }}>{c.label}</span>
                      </td>
                      <td style={{ ...estiloPivotTdNum, cursor: "pointer", background: cFondo }} onClick={() => onFiltrar(cSeleccionado ? "aliado" : "ciudad", cSeleccionado ? a.label : c.label, a.label)}>{c.total}</td>
                      <td style={{ ...estiloPivotTdNum, cursor: "pointer", background: cFondo }} onClick={() => onFiltrar(cSeleccionado ? "aliado" : "ciudad", cSeleccionado ? a.label : c.label, a.label)}>
                        {c.devoluciones > 0 ? <Tag text={`${c.devoluciones}×`} bg="#ffedd5" color="#c2410c" /> : "—"}
                      </td>
                    </tr>
                    {cAbierto && c.nodos.map((n) => {
                      const nSeleccionado = filtroAliado === a.label && filtroCiudad === c.label && filtroNodo === n.label;
                      return (
                        <tr key={n.label} style={nSeleccionado ? { outline: "2px solid #0f172a", outlineOffset: "-2px" } : undefined}>
                          <td style={{ ...estiloPivotTd, paddingLeft: "54px", cursor: "pointer", background: nSeleccionado ? "#fde8e8" : undefined }}
                            onClick={() => onFiltrar(nSeleccionado ? "ciudad" : "nodo", nSeleccionado ? c.label : n.label, a.label, c.label)}>
                            {n.label}
                          </td>
                          <td style={{ ...estiloPivotTdNum, cursor: "pointer", background: nSeleccionado ? "#fde8e8" : undefined }}
                            onClick={() => onFiltrar(nSeleccionado ? "ciudad" : "nodo", nSeleccionado ? c.label : n.label, a.label, c.label)}>{n.total}</td>
                          <td style={{ ...estiloPivotTdNum, cursor: "pointer", background: nSeleccionado ? "#fde8e8" : undefined }}
                            onClick={() => onFiltrar(nSeleccionado ? "ciudad" : "nodo", nSeleccionado ? c.label : n.label, a.label, c.label)}>
                            {n.devoluciones > 0 ? <Tag text={`${n.devoluciones}×`} bg="#ffedd5" color="#c2410c" /> : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
            </Fragment>
          );
        })}
        {datos.length === 0 && (
          <tr><td colSpan={3} style={{ ...estiloPivotTd, textAlign: "center", color: CLARO_MUTED }}>Sin datos para este filtro.</td></tr>
        )}
        <tr>
          <td style={{ ...estiloPivotTd, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={onLimpiar} title="Quitar filtro (ver todo)">Total general</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={onLimpiar}>{totalGeneral}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={onLimpiar}>{devolucionesGeneral}</td>
        </tr>
      </tbody>
    </MarcoTablaPivote>
  );
}

// ── Selector de nodo con búsqueda (la lista de nodos puede ser larga) ──
// `destacados` (opcional): nodos priorizados y pendientes de certificar
// este ciclo — se muestran primero, con una etiqueta de su mes de
// priorización, para amarrar el caso al nodo correcto desde el principio
// en vez de tener que adivinarlo después.
function SelectorNodo({ nodos, destacados = [], valor, onSeleccionar, disabled }) {
  const [busqueda, setBusqueda] = useState("");
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const cerrar = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", cerrar);
    return () => document.removeEventListener("mousedown", cerrar);
  }, []);

  const nodoActual = nodos.find((n) => n.id_nodo === valor) || destacados.find((n) => n.id_nodo === valor);
  const mesDelActual = destacados.find((n) => n.id_nodo === valor)?.mes_priorizacion;
  const q = busqueda.trim().toLowerCase();
  const coincide = (n) =>
    n.id_nodo?.toLowerCase().includes(q) ||
    n.NOMBRE_NODO?.toLowerCase().includes(q) ||
    n.MUNICIPIO?.toLowerCase().includes(q);

  const destacadosFiltrados = (q ? destacados.filter(coincide) : destacados).slice(0, 30);
  const idsDestacados = new Set(destacadosFiltrados.map((n) => n.id_nodo));
  const otrosResultados = q
    ? nodos.filter((n) => coincide(n) && !idsDestacados.has(n.id_nodo)).slice(0, 30)
    : [];

  if (valor && nodoActual && !abierto) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "9px 12px", border: `1px solid ${CLARO_RED}`, borderRadius: "8px", background: "#fef2f2" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "13px", fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "6px" }}>
            {nodoActual.id_nodo}
            {mesDelActual && <Tag text={`Priorizado · ${formatoMes(mesDelActual)}`} bg="#e0f2fe" color="#075985" />}
          </div>
          <div style={{ fontSize: "11px", color: CLARO_MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {nodoActual.NOMBRE_NODO} · {nodoActual.MUNICIPIO}
          </div>
        </div>
        {!disabled && (
          <button type="button" onClick={() => { onSeleccionar(""); setAbierto(true); }}
            style={{ border: "none", background: "white", color: CLARO_MUTED, borderRadius: "6px", padding: "4px 8px", cursor: "pointer", fontSize: "11px" }}>
            Cambiar
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        type="text"
        value={busqueda}
        onChange={(e) => { setBusqueda(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        placeholder={destacados.length > 0 ? "Busca, o elige uno de los priorizados pendientes…" : "Busca por nodo, nombre o ciudad…"}
        disabled={disabled}
        style={{ ...inputSt, width: "100%" }}
      />
      {abierto && (destacadosFiltrados.length > 0 || otrosResultados.length > 0) && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "white", border: `1px solid ${BORDER}`, borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: "280px", overflowY: "auto", zIndex: 20 }}>
          {destacadosFiltrados.length > 0 && (
            <div style={{ padding: "6px 10px 2px", fontSize: "10px", fontWeight: "700", color: "#075985", textTransform: "uppercase", letterSpacing: "0.03em" }}>
              Priorizados pendientes de certificar
            </div>
          )}
          {destacadosFiltrados.map((n) => (
            <div key={`dest-${n.id_nodo}`} onClick={() => { onSeleccionar(n.id_nodo); setBusqueda(""); setAbierto(false); }}
              style={{ padding: "8px 10px", cursor: "pointer", borderBottom: `1px solid ${BORDER_SOFT}`, fontSize: "12px", background: "#f0f9ff" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#e0f2fe"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#f0f9ff"}
            >
              <div style={{ fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "6px" }}>
                {n.id_nodo} <Tag text={formatoMes(n.mes_priorizacion)} bg="#e0f2fe" color="#075985" />
              </div>
              <div style={{ color: CLARO_MUTED, fontSize: "11px" }}>{n.NOMBRE_NODO} · {n.MUNICIPIO} · {n.Aliado_Rexterna}</div>
            </div>
          ))}
          {otrosResultados.length > 0 && (
            <div style={{ padding: "6px 10px 2px", fontSize: "10px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", letterSpacing: "0.03em" }}>
              Otros nodos
            </div>
          )}
          {otrosResultados.map((n) => (
            <div key={n.id_nodo} onClick={() => { onSeleccionar(n.id_nodo); setBusqueda(""); setAbierto(false); }}
              style={{ padding: "8px 10px", cursor: "pointer", borderBottom: `1px solid ${BORDER_SOFT}`, fontSize: "12px" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
              onMouseLeave={(e) => e.currentTarget.style.background = "white"}
            >
              <div style={{ fontWeight: "700", color: CLARO_DARK }}>{n.id_nodo}</div>
              <div style={{ color: CLARO_MUTED, fontSize: "11px" }}>{n.NOMBRE_NODO} · {n.MUNICIPIO} · {n.Aliado_Rexterna}</div>
            </div>
          ))}
        </div>
      )}
      {abierto && q && destacadosFiltrados.length === 0 && otrosResultados.length === 0 && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "white", border: `1px solid ${BORDER}`, borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: "10px", fontSize: "12px", color: CLARO_MUTED, zIndex: 20 }}>
          Sin resultados para "{busqueda}".
        </div>
      )}
    </div>
  );
}

// ── Un campo del trío TAS / OT / CHG, con su propio icono ──────────
function CampoIdentificador({ icono, label, valor, onChange, requerido }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <label style={{ ...labelSt, display: "flex", alignItems: "center", gap: "5px" }}>
        <span style={{ fontSize: "13px" }}>{icono}</span> {label}{requerido && <span style={{ color: "#dc2626" }}>*</span>}
      </label>
      <input type="text" value={valor || ""} onChange={onChange} style={{ ...inputSt, width: "100%" }} />
    </div>
  );
}

const ICONOS_EVENTO = { creacion: "🆕", cambio_estado: "🔄", devolucion: "↩️" };

// ── Línea de tiempo del historial de un caso ──────────────────────
function LineaTiempoEscalamiento({ eventos, cargando }) {
  if (cargando) return <div style={{ color: CLARO_MUTED, fontSize: "13px" }}>Cargando historial…</div>;
  if (!eventos || eventos.length === 0) return <div style={{ color: CLARO_MUTED, fontSize: "13px" }}>Sin eventos registrados.</div>;
  const ICONOS = ICONOS_EVENTO;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {eventos.map((e, i) => (
        <div key={e.id} style={{ display: "flex", gap: "14px" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ width: "11px", height: "11px", borderRadius: "50%", background: i === 0 ? CLARO_RED : "#cbd5e1", marginTop: "3px", flexShrink: 0 }} />
            {i < eventos.length - 1 && <div style={{ width: "2px", flex: 1, background: BORDER }} />}
          </div>
          <div style={{ paddingBottom: "18px" }}>
            <div style={{ fontSize: "11px", color: CLARO_MUTED, marginBottom: "3px" }}>
              {formatoFecha(e.fecha)} · {e.usuario_nombre || "—"} ({e.usuario_rol || "—"})
            </div>
            <div style={{ background: i === 0 ? "#fef2f2" : "#f8fafc", border: `1px solid ${i === 0 ? "#fca5a5" : BORDER}`, borderRadius: "10px", padding: "10px 12px", fontSize: "12.5px", color: CLARO_DARK, maxWidth: "520px", wordBreak: "break-word" }}>
              <span style={{ marginRight: "6px" }}>{ICONOS[e.tipo_evento] || "•"}</span>
              {e.nota || notaPorDefecto(e)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
function notaPorDefecto(e) {
  if (e.tipo_evento === "cambio_estado") {
    const al = ESTADO_INFO[e.estado_nuevo]?.label || e.estado_nuevo || "—";
    const desde = ESTADO_INFO[e.estado_anterior]?.label || e.estado_anterior || "—";
    return `${desde} → ${al}`;
  }
  return "—";
}

export default function Escalamientos({ usuario }) {
  const esAdmin = (usuario?.rol || "").toLowerCase().trim() === "administrador";
  // Certificar un nodo es una acción restringida (por defecto solo Admin;
  // el Admin puede habilitarla puntualmente a otros desde Gestión de
  // Usuarios) — se oculta la opción "Nodo Certificado" de quien no la
  // tiene, y el servidor la rechaza igual aunque alguien la fuerce.
  const puedeCertificar = puedeCertificarEscalamiento(usuario);

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const [nodos, setNodos] = useState([]);
  // Nodos priorizados en su mes más reciente que todavía NO tienen
  // certificación registrada — se le destacan primero al crear un caso de
  // Certificación Huella, para amarrar el caso al nodo/mes correcto desde
  // el inicio y no tener que adivinarlo o duplicarlo después.
  const [nodosPendientes, setNodosPendientes] = useState([]);

  // Filtros
  const [filtroEstado, setFiltroEstado]   = useState("");
  const [filtroTipo, setFiltroTipo]       = useState("");
  const [filtroAliado, setFiltroAliado]   = useState("");
  const [filtroJefe, setFiltroJefe]       = useState("");
  const [filtroCiudad, setFiltroCiudad]   = useState("");
  const [busqueda, setBusqueda]           = useState("");
  const [desde, setDesde]                 = useState("");
  const [hasta, setHasta]                 = useState("");
  // Atajo visual sobre desde/hasta: "Todos" los limpia, "Hoy"/"Última
  // semana"/"Este mes" los calculan solos, y "Personalizado" deja los 2
  // campos de fecha a la vista para elegir un rango propio — así casi nadie
  // tiene que tocar un selector de fecha para lo que usa todos los días.
  const [rangoRapido, setRangoRapido] = useState("todos");
  const aplicarRangoRapido = (tipo) => {
    setRangoRapido(tipo);
    if (tipo === "personalizado") return;
    const pad = (n) => String(n).padStart(2, "0");
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const hoy = new Date();
    if (tipo === "todos") { setDesde(""); setHasta(""); return; }
    if (tipo === "hoy") { setDesde(iso(hoy)); setHasta(iso(hoy)); return; }
    if (tipo === "semana") { const ini = new Date(hoy); ini.setDate(hoy.getDate() - 6); setDesde(iso(ini)); setHasta(iso(hoy)); return; }
    if (tipo === "mes") { setDesde(iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1))); setHasta(iso(hoy)); return; }
  };

  const cargarNodosPendientes = () => {
    fetch(`${API}/huella-nodos-priorizados-pendientes`).then((r) => r.json()).then((d) => setNodosPendientes(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const cargarEscalamientos = () => {
    setCargando(true);
    const p = new URLSearchParams();
    if (filtroEstado) p.set("estado", filtroEstado);
    if (filtroTipo)   p.set("tipo_solicitud", filtroTipo);
    if (filtroAliado) p.set("aliado", filtroAliado);
    if (filtroJefe)   p.set("jefe", filtroJefe);
    if (filtroCiudad) p.set("ciudad", filtroCiudad);
    if (busqueda)     p.set("q", busqueda);
    if (desde)        p.set("desde", desde);
    if (hasta)        p.set("hasta", hasta);
    fetch(`${API}/huella-escalamientos?${p.toString()}`)
      .then((r) => r.json())
      .then((data) => { if (data.ok) setFilas(data.filas || []); else setError(data.mensaje); })
      .catch(() => setError("No se pudo conectar con el servidor."))
      .finally(() => setCargando(false));
  };

  useEffect(() => { cargarEscalamientos(); /* eslint-disable-next-line */ }, [filtroEstado, filtroTipo, filtroAliado, filtroJefe, filtroCiudad, desde, hasta]);
  // Búsqueda libre: con un pequeño retraso para no disparar una consulta por cada tecla.
  useEffect(() => {
    const t = setTimeout(() => cargarEscalamientos(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [busqueda]);

  useEffect(() => {
    fetch(`${API}/huella-nodos-red`).then((r) => r.json()).then((d) => setNodos(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/huella-nodos-priorizados-pendientes`).then((r) => r.json()).then((d) => setNodosPendientes(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // Opciones de filtro: de los datos ya cargados (igual que en Calidad Nodos Huella)
  const aliados = useMemo(() => [...new Set(filas.map((f) => f.aliado).filter(Boolean))].sort(), [filas]);
  const jefes   = useMemo(() => [...new Set(filas.map((f) => f.jefe_zona).filter(Boolean))].sort(), [filas]);
  const ciudades = useMemo(() => [...new Set(filas.map((f) => f.ciudad).filter(Boolean))].sort(), [filas]);

  // ── KPIs ──────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total = filas.length;
    const enRevision = filas.filter((f) => f.estado === "En Revision").length;
    const escaladoNoc = filas.filter((f) => f.estado === "Escalado NOC").length;
    const certificados = filas.filter((f) => f.estado === "Nodo Certificado").length;
    const devueltos = filas.filter((f) => f.estado === "Devuelto").length;
    const resueltos = filas.filter((f) => f.estado === "Resuelto").length;
    const abiertos = enRevision + escaladoNoc;

    const tiemposHastaNoc = filas.filter((f) => f.minutos_hasta_escalado_noc != null).map((f) => f.minutos_hasta_escalado_noc);
    const promHastaNoc = tiemposHastaNoc.length ? tiemposHastaNoc.reduce((s, v) => s + v, 0) / tiemposHastaNoc.length : null;

    const tiemposResolucion = filas.filter((f) => (f.estado === "Nodo Certificado" || f.estado === "Devuelto" || f.estado === "Resuelto") && f.minutos_resolucion != null).map((f) => f.minutos_resolucion);
    const promResolucion = tiemposResolucion.length ? tiemposResolucion.reduce((s, v) => s + v, 0) / tiemposResolucion.length : null;

    // Tiempo de respuesta propio: solo los casos que se resolvieron directo
    // (sin pasar por el NOC) — útil para medir cuánto te demoras tú en
    // gestionar, por ejemplo, una Creación TAS.
    const tiemposPropios = filas.filter((f) => f.estado === "Resuelto" && f.fecha_escalado_noc == null && f.minutos_resolucion != null).map((f) => f.minutos_resolucion);
    const promTiempoPropio = tiemposPropios.length ? tiemposPropios.reduce((s, v) => s + v, 0) / tiemposPropios.length : null;

    // Total de devoluciones: suma el contador de CADA caso, así que un caso
    // que se devolvió 2 veces (se reescaló y lo devolvieron otra vez) cuenta
    // 2 — no es lo mismo que "Devueltos" de arriba, que es cuántos casos
    // están ahora mismo en ese estado.
    const totalDevoluciones = filas.reduce((s, f) => s + (f.veces_devuelto || 0), 0);
    const casosConDevolucion = filas.filter((f) => (f.veces_devuelto || 0) > 0).length;

    return { total, abiertos, certificados, devueltos, resueltos, promHastaNoc, promResolucion, promTiempoPropio, totalDevoluciones, casosConDevolucion };
  }, [filas]);

  // ── Casos "estancados": abiertos (En Revisión / Escalado NOC) sin ningún
  // movimiento (cambio de estado) en los últimos DIAS_ESTANCADO días — para
  // avisar de un vistazo cuáles llevan quietos demasiado tiempo, sin tener
  // que entrar caso por caso a revisar el historial. Usa `ultima_nota_fecha`
  // (la fecha del último evento del historial, que ya trae el backend) y,
  // si el caso nunca tuvo ningún evento aparte de la creación, cae a la
  // fecha de creación.
  const DIAS_ESTANCADO = 3;
  const casosEstancados = useMemo(() => {
    const limite = Date.now() - DIAS_ESTANCADO * 24 * 60 * 60 * 1000;
    return filas.filter((f) => {
      if (f.estado !== "En Revision" && f.estado !== "Escalado NOC") return false;
      const fechaRef = f.ultima_nota_fecha || f.fecha_escalamiento;
      if (!fechaRef) return false;
      const t = new Date(String(fechaRef).replace(" ", "T")).getTime();
      return !isNaN(t) && t < limite;
    });
  }, [filas]);

  // ── Resumen de devoluciones, agrupado por Nodo / Aliado / Ciudad: cuántos
  // casos y cuántas devoluciones acumula cada uno — vive en su propia
  // pestaña "Devoluciones" (ver más abajo), separado de la lista normal de
  // casos. Creación TAS y Certificación Huella se devuelven por motivos muy
  // distintos (gestión propia vs. certificación del NOC), así que esta
  // pestaña tiene su PROPIO sub-filtro de tipo (independiente de filtroTipo,
  // que es el de la vista "lista") para no mezclar ambos en una sola cifra.
  const [filtroTipoDevol, setFiltroTipoDevol] = useState("");
  const filasParaDevoluciones = useMemo(
    () => (filtroTipoDevol ? filas.filter((f) => f.tipo_solicitud === filtroTipoDevol) : filas),
    [filas, filtroTipoDevol]
  );
  const kpisDevoluciones = useMemo(() => ({
    total: filasParaDevoluciones.reduce((s, f) => s + (f.veces_devuelto || 0), 0),
    casos: filasParaDevoluciones.filter((f) => (f.veces_devuelto || 0) > 0).length,
  }), [filasParaDevoluciones]);

  // ── Tabla dinámica Aliado → Ciudad → Nodo (estilo "tabla pivote" de
  // Backlog Regional): cada Aliado es la fila principal, con sus Ciudades
  // desplegables debajo y, dentro de cada Ciudad, sus Nodos. Cualquier fila
  // (nombre o cifras) funciona como filtro de la tabla de detalle de abajo;
  // las flechitas solo despliegan/contraen, sin filtrar.
  const [filtroAliadoDevol, setFiltroAliadoDevol] = useState(null);
  const [filtroCiudadDevol, setFiltroCiudadDevol] = useState(null);
  const [filtroNodoDevol, setFiltroNodoDevol] = useState(null);
  // Si se cambia de sub-filtro de tipo (Todos/Creación TAS/Certificación),
  // se limpia el drill-down para no quedar "filtrando" un nodo que ya no
  // aplica a ese tipo.
  useEffect(() => {
    setFiltroAliadoDevol(null); setFiltroCiudadDevol(null); setFiltroNodoDevol(null);
  }, [filtroTipoDevol]);

  const resumenAliadoCiudadNodo = useMemo(() => {
    const porAliado = {};
    filasParaDevoluciones.forEach((f) => {
      const aKey = f.aliado || "Sin aliado";
      const cKey = f.ciudad || "Sin ciudad";
      const nKey = f.nodo || "Sin nodo";
      if (!porAliado[aKey]) porAliado[aKey] = { total: 0, devoluciones: 0, ciudades: {} };
      if (!porAliado[aKey].ciudades[cKey]) porAliado[aKey].ciudades[cKey] = { total: 0, devoluciones: 0, nodos: {} };
      if (!porAliado[aKey].ciudades[cKey].nodos[nKey]) porAliado[aKey].ciudades[cKey].nodos[nKey] = { total: 0, devoluciones: 0 };
      const dev = f.veces_devuelto || 0;
      porAliado[aKey].total += 1; porAliado[aKey].devoluciones += dev;
      porAliado[aKey].ciudades[cKey].total += 1; porAliado[aKey].ciudades[cKey].devoluciones += dev;
      porAliado[aKey].ciudades[cKey].nodos[nKey].total += 1; porAliado[aKey].ciudades[cKey].nodos[nKey].devoluciones += dev;
    });
    const ordenar = (arr) => arr.sort((a, b) => b.devoluciones - a.devoluciones || b.total - a.total);
    return ordenar(Object.entries(porAliado).map(([label, v]) => ({
      label, total: v.total, devoluciones: v.devoluciones,
      ciudades: ordenar(Object.entries(v.ciudades).map(([cLabel, cv]) => ({
        label: cLabel, total: cv.total, devoluciones: cv.devoluciones,
        nodos: ordenar(Object.entries(cv.nodos).map(([nLabel, nv]) => ({ label: nLabel, total: nv.total, devoluciones: nv.devoluciones }))),
      }))),
    })));
  }, [filasParaDevoluciones]);

  // Tabla de detalle de abajo: los casos que coinciden con lo filtrado en la
  // tabla dinámica de arriba (o todos, si no hay nada filtrado).
  const filasDetalleDevol = useMemo(() => filasParaDevoluciones.filter((f) => {
    if (filtroAliadoDevol && (f.aliado || "Sin aliado") !== filtroAliadoDevol) return false;
    if (filtroCiudadDevol && (f.ciudad || "Sin ciudad") !== filtroCiudadDevol) return false;
    if (filtroNodoDevol && (f.nodo || "Sin nodo") !== filtroNodoDevol) return false;
    return true;
  }), [filasParaDevoluciones, filtroAliadoDevol, filtroCiudadDevol, filtroNodoDevol]);

  // Clic en una fila de la tabla dinámica: "aliado" fija solo el aliado;
  // "ciudad" fija aliado+ciudad (para no mezclar ciudades con el mismo
  // nombre de aliados distintos); "nodo" fija los 3 niveles; null limpia
  // todo (fila roja "Total general").
  const filtrarPivoteDevol = (nivel, label, aliadoPadre, ciudadPadre) => {
    if (nivel === null) { setFiltroAliadoDevol(null); setFiltroCiudadDevol(null); setFiltroNodoDevol(null); return; }
    if (nivel === "aliado") { setFiltroAliadoDevol(label); setFiltroCiudadDevol(null); setFiltroNodoDevol(null); return; }
    if (nivel === "ciudad") { setFiltroAliadoDevol(aliadoPadre); setFiltroCiudadDevol(label); setFiltroNodoDevol(null); return; }
    if (nivel === "nodo") { setFiltroAliadoDevol(aliadoPadre); setFiltroCiudadDevol(ciudadPadre); setFiltroNodoDevol(label); return; }
  };
  const limpiarPivoteDevol = () => { setFiltroAliadoDevol(null); setFiltroCiudadDevol(null); setFiltroNodoDevol(null); };

  // ── Pestaña principal: "lista" es la vista de siempre (KPIs + filtros +
  // tabla de casos); "devoluciones" es la vista nueva, dedicada solo a ver
  // quién/qué nodo acumula más devoluciones. Independiente de filtroTipo
  // (Todos / Certificación / Creación TAS), que sigue siendo un sub-filtro
  // de la vista "lista".
  const [tabPrincipal, setTabPrincipal] = useState("lista");

  // Simplicidad para el día a día: solo las 5 tarjetas esenciales están
  // siempre visibles; el resto de métricas quedan detrás de "Ver más
  // detalle". Igual con las 2 columnas de tiempos de la tabla (NOC /
  // resolución), que solo se muestran si se piden — para que la tabla no
  // se vea tan cargada de entrada, sobre todo en pantallas chicas.
  const [mostrarKpisDetalle, setMostrarKpisDetalle] = useState(false);
  const [mostrarTiempos, setMostrarTiempos] = useState(false);

  // ── Formulario crear/editar ───────────────────────────────────
  const [formAbierto, setFormAbierto] = useState(null); // null | "nuevo" | fila a editar
  const [formDatos, setFormDatos] = useState({});
  const [guardandoForm, setGuardandoForm] = useState(false);
  const [errorForm, setErrorForm] = useState(null);

  // La fecha de creación NUNCA se edita a mano — siempre se toma la hora
  // real del servidor en el momento de guardar, para que quede el dato
  // real de gestión. El campo del formulario solo es informativo
  // (muestra la hora actual mientras se diligencia) y está deshabilitado.
  const abrirNuevo = () => {
    setFormDatos({ nodo: "", tipo_solicitud: "", tas: "", ot: "", chg: "", descripcion: "", fecha_escalamiento: ahoraDatetimeLocal() });
    setErrorForm(null);
    setFormAbierto("nuevo");
  };
  const abrirEditar = (f) => {
    setFormDatos({
      nodo: f.nodo, tipo_solicitud: f.tipo_solicitud || "", tas: f.tas || "", ot: f.ot || "", chg: f.chg || "",
      descripcion: f.descripcion || "", fecha_escalamiento: aDatetimeLocal(f.fecha_escalamiento),
    });
    setErrorForm(null);
    setFormAbierto(f);
  };
  const cerrarForm = () => { setFormAbierto(null); setFormDatos({}); setErrorForm(null); };

  const esCertificacionHuella = formDatos.tipo_solicitud === "Certificación Huella";

  const guardarForm = () => {
    if (!formDatos.nodo) { setErrorForm("Elige el nodo."); return; }
    if (!formDatos.tipo_solicitud) { setErrorForm("Elige el tipo de solicitud."); return; }
    if (esCertificacionHuella) {
      if (!formDatos.tas || !formDatos.tas.trim()) { setErrorForm("Para Certificación Huella, el TAS es obligatorio."); return; }
      if (!formDatos.ot || !formDatos.ot.trim())   { setErrorForm("Para Certificación Huella, la OT es obligatoria."); return; }
      if (!formDatos.chg || !formDatos.chg.trim()) { setErrorForm("Para Certificación Huella, el CHG es obligatorio."); return; }
    }
    setGuardandoForm(true);
    setErrorForm(null);
    const esNuevo = formAbierto === "nuevo";
    // Nunca se manda una fecha editada a mano: en un caso nuevo, no se
    // envía — el backend toma la hora real del servidor al guardar. Al
    // editar, tampoco se manda — la fecha de creación original no se toca.
    const fechaParaGuardar = null;
    const body = {
      nodo: formDatos.nodo, tipo_solicitud: formDatos.tipo_solicitud, tas: formDatos.tas, ot: formDatos.ot, chg: formDatos.chg,
      descripcion: formDatos.descripcion, fecha_escalamiento: fechaParaGuardar,
      rol: usuario?.rol, cedula: usuario?.cedula, nombre_usuario: usuario?.nombre,
    };
    const url = esNuevo ? `${API}/huella-escalamientos` : `${API}/huella-escalamientos/${formAbierto.id}`;
    fetch(url, { method: esNuevo ? "POST" : "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => r.json())
      .then((data) => {
        if (!data.ok) { setErrorForm(data.mensaje || "No se pudo guardar."); return; }
        cerrarForm();
        cargarEscalamientos();
      })
      .catch(() => setErrorForm("No se pudo conectar con el servidor."))
      .finally(() => setGuardandoForm(false));
  };

  // ── Cambiar estado directo desde la tabla ─────────────────────
  // Cada cambio de estado abre un cuadro pidiendo una observación (ver
  // `cambioPendiente` más abajo) — esa nota queda guardada en el historial
  // del caso, para poder ver después por qué se devolvió, cómo se escaló o
  // cómo quedó certificado/resuelto cada vez.
  const cambiarEstado = (f, nuevoEstado, nota) => {
    if (nuevoEstado === f.estado) return;
    setFilas((prev) => prev.map((x) => (x.id === f.id
      ? { ...x, estado: nuevoEstado, veces_devuelto: nuevoEstado === "Devuelto" ? (x.veces_devuelto || 0) + 1 : x.veces_devuelto }
      : x)));
    fetch(`${API}/huella-escalamientos/${f.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ estado: nuevoEstado, nota, rol: usuario?.rol, cedula: usuario?.cedula, nombre_usuario: usuario?.nombre }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data.ok) { window.alert("⚠️ No se pudo cambiar el estado: " + (data.mensaje || "")); }
        // Por ahora Escalamientos NO está vinculado con Priorización Huella
        // (se desactivó el registro automático de certificación), así que
        // esto normalmente no entra, pero se deja por si se reactiva luego.
        else if (data.certificacion?.mensaje) { window.alert(data.certificacion.mensaje); cargarNodosPendientes(); }
        cargarEscalamientos();
      })
      .catch(() => { window.alert("⚠️ No se pudo conectar con el servidor."); cargarEscalamientos(); });
  };

  // Al cambiar de estado siempre se pregunta por una observación — para
  // "Devuelto" sirve para dejar el motivo; para "Escalado NOC", "Nodo
  // Certificado" o "Resuelto" sirve para anotar qué se hizo o con quién se
  // gestionó. Queda guardada en el historial del caso.
  const [cambioPendiente, setCambioPendiente] = useState(null); // fila o null
  const [estadoPendiente, setEstadoPendiente] = useState(null);
  const [notaCambio, setNotaCambio] = useState("");
  const manejarCambioEstado = (f, nuevoEstado) => {
    if (nuevoEstado === f.estado) return;
    setCambioPendiente(f);
    setEstadoPendiente(nuevoEstado);
    setNotaCambio("");
  };
  const confirmarCambioEstado = () => {
    const notaPorDefectoSegunEstado = estadoPendiente === "Devuelto" ? "Caso devuelto" : undefined;
    cambiarEstado(cambioPendiente, estadoPendiente, notaCambio.trim() || notaPorDefectoSegunEstado);
    setCambioPendiente(null);
    setEstadoPendiente(null);
    setNotaCambio("");
  };
  const TEXTOS_MODAL_CAMBIO_ESTADO = {
    "Devuelto":         { titulo: "¿Por qué se devuelve este caso?", placeholder: "Ej: Falta documentación del nodo, equipo no certificado, etc." },
    "Escalado NOC":     { titulo: "Observación del escalamiento al NOC", placeholder: "Ej: Se escaló con el equipo X, ticket radicado, etc. (opcional)" },
    "Nodo Certificado": { titulo: "Observación de la certificación", placeholder: "Ej: Certificado por el NOC el día tal, con quién se validó, etc. (opcional)" },
    "Resuelto":         { titulo: "Observación de la resolución", placeholder: "Ej: Qué se hizo para resolver el caso. (opcional)" },
    "En Revision":      { titulo: "Observación", placeholder: "Detalle del cambio de estado (opcional)" },
  };

  // ── Historial de un caso ───────────────────────────────────────
  const [historialAbierto, setHistorialAbierto] = useState(null); // fila o null
  const [historialDatos, setHistorialDatos] = useState([]);
  const [historialCargando, setHistorialCargando] = useState(false);
  const abrirHistorial = (f) => {
    setHistorialAbierto(f);
    setHistorialCargando(true);
    fetch(`${API}/huella-escalamientos/${f.id}/historial`)
      .then((r) => r.json())
      .then((data) => setHistorialDatos(data.ok ? data.filas : []))
      .catch(() => setHistorialDatos([]))
      .finally(() => setHistorialCargando(false));
  };

  // ── Eliminar (solo Admin) ───────────────────────────────────────
  const eliminar = (f) => {
    if (!window.confirm(`¿Eliminar el escalamiento del nodo ${f.nodo}? Esta acción no se puede deshacer.`)) return;
    fetch(`${API}/huella-escalamientos/${f.id}`, {
      method: "DELETE", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rol: usuario?.rol, cedula: usuario?.cedula }),
    })
      .then((r) => r.json())
      .then((data) => { if (!data.ok) window.alert("⚠️ " + (data.mensaje || "No se pudo eliminar.")); else cargarEscalamientos(); })
      .catch(() => window.alert("⚠️ No se pudo conectar con el servidor."));
  };

  return (
    <div style={{ padding: "24px 28px", fontFamily: "'Segoe UI', Arial, sans-serif", maxWidth: 1650, margin: "0 auto" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "22px", fontWeight: "700", color: CLARO_DARK }}>📨 Escalamientos</h1>
          <p style={{ margin: "4px 0 0 0", color: CLARO_MUTED, fontSize: "13px" }}>
            Seguimiento de TAS, OT y CHG de los nodos escalados de la huella — estado, tiempos de respuesta y devoluciones.
          </p>
        </div>
        <button onClick={abrirNuevo} style={{
          padding: "9px 18px", borderRadius: "8px", border: "none", cursor: "pointer",
          background: `linear-gradient(135deg, ${CLARO_RED}, #B0000E)`, color: "white",
          fontSize: "13px", fontWeight: "700", boxShadow: "0 2px 8px rgba(227,6,19,0.3)",
        }}>
          + Nuevo escalamiento
        </button>
      </div>

      {error && (
        <div style={{ padding: "10px 16px", borderRadius: "8px", marginBottom: "16px", background: "#fee2e2", color: "#991b1b", fontSize: "13px", fontWeight: "500", display: "flex", justifyContent: "space-between" }}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "16px", opacity: 0.6 }}>✕</button>
        </div>
      )}

      {/* ── Pestañas: "Todos" / "Certificación de nodos" / "Creación de TAS"
          son sub-filtros de la vista de lista (filtroTipo); "Devoluciones"
          es una vista aparte, dedicada solo a ver qué nodo/aliado/ciudad
          acumula más devoluciones. ── */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "18px", borderBottom: `2px solid ${BORDER}` }}>
        {[["", "Todos"], ["Certificación Huella", "Certificación de nodos"], ["Creación TAS", "Creación de TAS"]].map(([id, label]) => (
          <button key={id || "todos"} onClick={() => { setFiltroTipo(id); setTabPrincipal("lista"); }} style={{
            padding: "10px 18px", border: "none", background: "none", cursor: "pointer",
            fontSize: "13.5px", fontWeight: tabPrincipal === "lista" && filtroTipo === id ? "700" : "500",
            color: tabPrincipal === "lista" && filtroTipo === id ? CLARO_RED : CLARO_MUTED,
            borderBottom: tabPrincipal === "lista" && filtroTipo === id ? `3px solid ${CLARO_RED}` : "3px solid transparent",
            marginBottom: "-2px",
          }}>{label}</button>
        ))}
        <button onClick={() => setTabPrincipal("devoluciones")} style={{
          padding: "10px 18px", border: "none", background: "none", cursor: "pointer",
          fontSize: "13.5px", fontWeight: tabPrincipal === "devoluciones" ? "700" : "500",
          color: tabPrincipal === "devoluciones" ? CLARO_RED : CLARO_MUTED,
          borderBottom: tabPrincipal === "devoluciones" ? `3px solid ${CLARO_RED}` : "3px solid transparent",
          marginBottom: "-2px",
        }}>Devoluciones</button>
      </div>

      {tabPrincipal === "lista" && (
      <>
      {/* ── Leyenda del flujo: para que cualquiera entienda de un vistazo por
          qué cada tipo de solicitud tiene sus propios estados ── */}
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px", padding: "10px 14px", background: "#f8fafc", border: `1px solid ${BORDER}`, borderRadius: "10px", fontSize: "11.5px", color: CLARO_MUTED }}>
        <div><strong style={{ color: CLARO_DARK }}>Creación TAS:</strong> En Revisión → Resuelto / Devuelto</div>
        <div><strong style={{ color: CLARO_DARK }}>Certificación Huella:</strong> En Revisión → Escalado NOC → Nodo Certificado / Devuelto</div>
      </div>

      {/* ── Casos estancados: aviso visible solo si hay alguno ── */}
      {casosEstancados.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", borderRadius: "8px", marginBottom: "16px", background: "#fff7ed", border: "1px solid #fdba74", color: "#9a3412", fontSize: "13px", fontWeight: "600" }}>
          <span>⏳</span>
          <span>{casosEstancados.length} caso(s) llevan más de {DIAS_ESTANCADO} días sin movimiento — filtra por estado "En Revisión" o "Escalado NOC" para ubicarlos.</span>
        </div>
      )}

      {/* ── KPIs: las 5 esenciales siempre visibles; el resto, opcional ── */}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "10px", alignItems: "stretch" }}>
        <KpiCard label="Total escalamientos" value={kpis.total} bg="#f1f5f9" color={CLARO_DARK} />
        <KpiCard label="Abiertos" value={kpis.abiertos} bg="#e0f2fe" color="#075985" sub="En Revisión + Escalado NOC" />
        <KpiCard label="Estancados" value={casosEstancados.length} bg={casosEstancados.length > 0 ? "#fff7ed" : "#f1f5f9"} color={casosEstancados.length > 0 ? "#c2410c" : CLARO_MUTED} sub={`+${DIAS_ESTANCADO} días sin movimiento`} />
        <KpiCard label="Nodo Certificado" value={kpis.certificados} bg="#f0fdf4" color="#15803d"
          sub={kpis.total > 0 ? `${Math.round(kpis.certificados / kpis.total * 100)}% del total` : null} />
        <KpiCard label="Devueltos" value={kpis.devueltos} bg="#fef2f2" color="#991b1b"
          sub={kpis.totalDevoluciones > 0 ? "Ver detalle por nodo →" : null} onClick={() => setTabPrincipal("devoluciones")} />
      </div>
      <div style={{ marginBottom: "20px" }}>
        <button onClick={() => setMostrarKpisDetalle((v) => !v)} style={{
          border: "none", background: "none", color: CLARO_RED, cursor: "pointer", fontSize: "12px", fontWeight: "700", padding: "2px 0",
        }}>
          {mostrarKpisDetalle ? "▲ Ocultar detalle de tiempos" : "▼ Ver más detalle (tiempos y devoluciones)"}
        </button>
        {mostrarKpisDetalle && (
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px" }}>
            <KpiCard label="Resueltos (gestión propia)" value={kpis.resueltos} bg="#ede9fe" color="#6d28d9" sub="Sin pasar por el NOC" />
            <KpiCard label="Tiempo hasta Escalado NOC" value={formatoDuracion(kpis.promHastaNoc)} bg="#eff6ff" color="#1d4ed8" sub="Promedio desde que se crea" />
            <KpiCard label="Tiempo de resolución" value={formatoDuracion(kpis.promResolucion)} bg="#fff7ed" color="#c2410c" sub="Promedio desde Escalado NOC o creación" />
            <KpiCard label="Tiempo de respuesta propio" value={formatoDuracion(kpis.promTiempoPropio)} bg="#f5f3ff" color="#6d28d9" sub="Casos resueltos sin NOC" />
            <KpiCard label="Devoluciones" value={kpis.totalDevoluciones} bg="#ffedd5" color="#c2410c"
              sub={kpis.casosConDevolucion > 0 ? `${kpis.casosConDevolucion} caso(s) devuelto(s)` : null} />
          </div>
        )}
      </div>

      {/* ── Filtros ── */}
      <div style={{ ...card, marginBottom: "16px", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label style={labelSt}>Buscar (TAS / OT / CHG / nodo)</label>
          <input type="text" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Ej: TAS123456"
            style={{ ...inputSt, width: "200px" }} />
        </div>
        <div>
          <label style={labelSt}>Estado</label>
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} style={{ ...inputSt, width: "160px" }}>
            <option value="">Todos</option>
            {ESTADOS.map((e) => <option key={e} value={e}>{ESTADO_INFO[e].label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelSt}>Aliado</label>
          <select value={filtroAliado} onChange={(e) => setFiltroAliado(e.target.value)} style={{ ...inputSt, width: "160px" }}>
            <option value="">Todos</option>
            {aliados.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label style={labelSt}>Jefe de zona</label>
          <select value={filtroJefe} onChange={(e) => setFiltroJefe(e.target.value)} style={{ ...inputSt, width: "160px" }}>
            <option value="">Todos</option>
            {jefes.map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
        </div>
        <div>
          <label style={labelSt}>Ciudad</label>
          <select value={filtroCiudad} onChange={(e) => setFiltroCiudad(e.target.value)} style={{ ...inputSt, width: "150px" }}>
            <option value="">Todas</option>
            {ciudades.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={labelSt}>Período</label>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {[["todos", "Todos"], ["hoy", "Hoy"], ["semana", "Última semana"], ["mes", "Este mes"], ["personalizado", "Personalizado"]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => aplicarRangoRapido(id)} style={{
                border: `1px solid ${rangoRapido === id ? CLARO_RED : BORDER}`,
                background: rangoRapido === id ? "#fef2f2" : "white",
                color: rangoRapido === id ? CLARO_RED : CLARO_DARK,
                borderRadius: "20px", padding: "7px 13px", cursor: "pointer", fontSize: "12px", fontWeight: "600",
              }}>{label}</button>
            ))}
          </div>
        </div>
        {rangoRapido === "personalizado" && (
          <>
            <div>
              <label style={labelSt}>Desde</label>
              <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={inputSt} />
            </div>
            <div>
              <label style={labelSt}>Hasta</label>
              <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={inputSt} />
            </div>
          </>
        )}
        {(filtroEstado || filtroTipo || filtroAliado || filtroJefe || filtroCiudad || busqueda || desde || hasta) && (
          <button onClick={() => { setFiltroEstado(""); setFiltroTipo(""); setFiltroAliado(""); setFiltroJefe(""); setFiltroCiudad(""); setBusqueda(""); setDesde(""); setHasta(""); setRangoRapido("todos"); }}
            style={{ border: "none", background: "#f1f5f9", color: CLARO_MUTED, borderRadius: "8px", padding: "8px 12px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>
            Limpiar filtros
          </button>
        )}
      </div>

      {/* ── Tabla: en pantallas angostas (celular) se muestra como tarjetas
          en vez de tabla — una tabla de 13-15 columnas es ilegible en un
          celular aunque tenga scroll horizontal. El punto de quiebre (860px)
          se controla con CSS puro, sin depender de JS/resize. ── */}
      <style>{`
        .esc-tabla-ancha { display: block; }
        .esc-tarjetas-mobile { display: none; }
        @media (max-width: 860px) {
          .esc-tabla-ancha { display: none; }
          .esc-tarjetas-mobile { display: flex; }
        }
      `}</style>

      <div style={{ marginBottom: "10px" }}>
        <button onClick={() => setMostrarTiempos((v) => !v)} style={{
          border: "none", background: "none", color: CLARO_RED, cursor: "pointer", fontSize: "12px", fontWeight: "700", padding: "2px 0",
        }}>
          {mostrarTiempos ? "▲ Ocultar columnas de tiempos" : "▼ Mostrar columnas de tiempos (NOC / resolución)"}
        </button>
      </div>

      {/* ── Tabla (pantallas anchas) ── */}
      <div className="esc-tabla-ancha" style={{ ...card, padding: 0, overflow: "hidden" }}>
        {cargando ? (
          <div style={{ textAlign: "center", padding: "50px 20px", color: CLARO_MUTED }}>Cargando…</div>
        ) : filas.length === 0 ? (
          <div style={{ textAlign: "center", padding: "50px 20px", color: "#94a3b8" }}>
            <div style={{ fontSize: "40px", marginBottom: "10px" }}>📭</div>
            <p style={{ fontSize: "14px", margin: 0, fontWeight: "600" }}>No hay escalamientos registrados todavía.</p>
            <p style={{ fontSize: "12.5px", marginTop: "6px" }}>Usa <strong>+ Nuevo escalamiento</strong> para crear el primero.</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <Th>Nodo</Th><Th>Tipo</Th><Th>Aliado</Th><Th>Ciudad</Th>
                  <Th>TAS</Th><Th>OT</Th><Th>CHG</Th>
                  <Th>Estado</Th><Th>Última observación</Th><Th>Mes priorización</Th><Th>Creado el</Th>
                  {mostrarTiempos && <><Th>Tiempo hasta NOC</Th><Th>Tiempo resolución</Th></>}
                  <Th>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => {
                  const info = ESTADO_INFO[f.estado] || ESTADO_INFO["En Revision"];
                  return (
                    <tr key={f.id}>
                      <Td style={{ fontWeight: "700" }}>{f.nodo}</Td>
                      <Td>{f.tipo_solicitud || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Sin tipo</span>}</Td>
                      <Td>{f.aliado}</Td>
                      <Td>{f.ciudad}</Td>
                      <Td>{f.tas || "—"}</Td>
                      <Td>{f.ot || "—"}</Td>
                      <Td>{f.chg || "—"}</Td>
                      <Td>
                        {(() => {
                          const opciones = estadosParaTipo(f.tipo_solicitud).filter((e) => e !== "Nodo Certificado" || puedeCertificar);
                          const opcionesFinal = opciones.includes(f.estado) ? opciones : [...opciones, f.estado];
                          return (
                            <select value={f.estado} onChange={(e) => manejarCambioEstado(f, e.target.value)}
                              style={{ border: `1px solid ${info.color}33`, background: info.bg, color: info.color, borderRadius: "6px", padding: "4px 8px", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}>
                              {opcionesFinal.map((e) => <option key={e} value={e}>{ESTADO_INFO[e]?.label || e}</option>)}
                            </select>
                          );
                        })()}
                      </Td>
                      <Td style={{ maxWidth: "260px" }}>
                        {f.ultima_nota ? (
                          <div title={f.ultima_nota} style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
                            <span style={{ flexShrink: 0 }}>{ICONOS_EVENTO[f.ultima_nota_evento] || "•"}</span>
                            <span style={{
                              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                              overflow: "hidden", fontSize: "12px", color: CLARO_DARK, lineHeight: 1.35,
                            }}>{f.ultima_nota}</span>
                          </div>
                        ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                      </Td>
                      <Td>
                        {f.mes_priorizacion_nodo
                          ? <Tag text={formatoMes(f.mes_priorizacion_nodo)} bg="#e0f2fe" color="#075985" />
                          : <span style={{ color: "#cbd5e1" }}>Sin priorizar</span>}
                      </Td>
                      <Td>{formatoFecha(f.fecha_escalamiento)}</Td>
                      {mostrarTiempos && (
                        <>
                          <Td>
                            <span style={{ fontWeight: "600" }}>{formatoDuracion(f.minutos_hasta_escalado_noc)}</span>
                            {f.fecha_escalado_noc == null && f.estado === "Resuelto" && (
                              <div style={{ fontSize: "10px", color: CLARO_MUTED }}>no requirió NOC</div>
                            )}
                            {f.fecha_escalado_noc == null && f.estado !== "Nodo Certificado" && f.estado !== "Devuelto" && f.estado !== "Resuelto" && (
                              <div style={{ fontSize: "10px", color: CLARO_MUTED }}>aún no se escala</div>
                            )}
                          </Td>
                          <Td>
                            <span style={{ fontWeight: "600" }}>{formatoDuracion(f.minutos_resolucion)}</span>
                            {f.estado !== "Nodo Certificado" && f.estado !== "Devuelto" && f.estado !== "Resuelto" && (
                              <div style={{ fontSize: "10px", color: CLARO_MUTED }}>corriendo…</div>
                            )}
                          </Td>
                        </>
                      )}
                      <Td>
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          <button onClick={() => abrirEditar(f)} title="Editar datos"
                            style={{ border: `1px solid ${BORDER}`, background: "white", color: CLARO_DARK, borderRadius: "6px", padding: "4px 8px", cursor: "pointer", fontSize: "11px" }}>✎ Editar</button>
                          <button onClick={() => abrirHistorial(f)} title="Ver historial"
                            style={{ border: "none", background: "none", color: CLARO_RED, fontWeight: "700", fontSize: "11px", cursor: "pointer", padding: "4px 2px" }}>Historial →</button>
                          {esAdmin && (
                            <button onClick={() => eliminar(f)} title="Eliminar"
                              style={{ border: "none", background: "none", color: "#ef4444", cursor: "pointer", fontSize: "13px", padding: "4px 2px" }}>🗑</button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Tarjetas (celular): mismos datos esenciales que la tabla, en
          formato de lectura vertical — nodo, tipo, estado (editable), los
          3 identificadores, última observación y acciones. ── */}
      <div className="esc-tarjetas-mobile" style={{ flexDirection: "column", gap: "10px" }}>
        {cargando ? (
          <div style={{ textAlign: "center", padding: "30px 20px", color: CLARO_MUTED }}>Cargando…</div>
        ) : filas.length === 0 ? (
          <div style={{ ...card, textAlign: "center", padding: "30px 20px", color: "#94a3b8" }}>
            <div style={{ fontSize: "36px", marginBottom: "8px" }}>📭</div>
            <p style={{ fontSize: "13px", margin: 0, fontWeight: "600" }}>No hay escalamientos registrados todavía.</p>
          </div>
        ) : filas.map((f) => {
          const info = ESTADO_INFO[f.estado] || ESTADO_INFO["En Revision"];
          const opciones = estadosParaTipo(f.tipo_solicitud).filter((e) => e !== "Nodo Certificado" || puedeCertificar);
          const opcionesFinal = opciones.includes(f.estado) ? opciones : [...opciones, f.estado];
          return (
            <div key={f.id} style={{ ...card, padding: "14px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px", marginBottom: "8px" }}>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "800", color: CLARO_DARK }}>{f.nodo}</div>
                  <div style={{ fontSize: "11px", color: CLARO_MUTED }}>{f.tipo_solicitud || "Sin tipo"} · {f.aliado} · {f.ciudad}</div>
                </div>
                <select value={f.estado} onChange={(e) => manejarCambioEstado(f, e.target.value)}
                  style={{ border: `1px solid ${info.color}33`, background: info.bg, color: info.color, borderRadius: "6px", padding: "4px 6px", fontSize: "10.5px", fontWeight: "700", cursor: "pointer", flexShrink: 0 }}>
                  {opcionesFinal.map((e) => <option key={e} value={e}>{ESTADO_INFO[e]?.label || e}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", fontSize: "11.5px", color: CLARO_DARK, marginBottom: "8px" }}>
                <div>🎫 {f.tas || "—"}</div>
                <div>🧾 {f.ot || "—"}</div>
                <div>🔄 {f.chg || "—"}</div>
                {f.veces_devuelto > 0 && <Tag text={`${f.veces_devuelto}× devuelto`} bg="#ffedd5" color="#c2410c" />}
              </div>
              {f.ultima_nota && (
                <div style={{ fontSize: "11.5px", color: CLARO_MUTED, background: "#f8fafc", borderRadius: "8px", padding: "8px 10px", marginBottom: "8px" }}>
                  {ICONOS_EVENTO[f.ultima_nota_evento] || "•"} {f.ultima_nota}
                </div>
              )}
              <div style={{ display: "flex", gap: "10px", fontSize: "11px" }}>
                <button onClick={() => abrirEditar(f)} style={{ border: `1px solid ${BORDER}`, background: "white", color: CLARO_DARK, borderRadius: "6px", padding: "5px 10px", cursor: "pointer" }}>✎ Editar</button>
                <button onClick={() => abrirHistorial(f)} style={{ border: "none", background: "none", color: CLARO_RED, fontWeight: "700", cursor: "pointer", padding: "5px 2px" }}>Historial →</button>
                {esAdmin && <button onClick={() => eliminar(f)} style={{ border: "none", background: "none", color: "#ef4444", cursor: "pointer", padding: "5px 2px" }}>🗑 Eliminar</button>}
              </div>
            </div>
          );
        })}
      </div>
      </>
      )}

      {/* ══════ Pestaña: Devoluciones ══════ */}
      {tabPrincipal === "devoluciones" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ fontSize: "12.5px", color: CLARO_MUTED }}>
            Cuántos casos y cuántas devoluciones acumula cada Nodo, Aliado y Ciudad — ordenado de más a menos devoluciones. Creación TAS y Certificación de nodos se devuelven por motivos distintos, así que primero elige cuál quieres ver.
          </div>

          {/* Sub-filtro propio de esta pestaña: separa Creación TAS de
              Certificación de nodos, para no mezclar sus devoluciones en
              una sola cifra. */}
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {[["", "Todos"], ["Creación TAS", "Creación TAS"], ["Certificación Huella", "Certificación de nodos"]].map(([id, label]) => (
              <button key={id || "todos-devol"} onClick={() => setFiltroTipoDevol(id)} style={{
                border: `1px solid ${filtroTipoDevol === id ? CLARO_RED : BORDER}`,
                background: filtroTipoDevol === id ? "#fef2f2" : "white",
                color: filtroTipoDevol === id ? CLARO_RED : CLARO_DARK,
                borderRadius: "20px", padding: "7px 14px", cursor: "pointer", fontSize: "12.5px", fontWeight: "600",
              }}>{label}</button>
            ))}
          </div>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <KpiCard label="Total devoluciones" value={kpisDevoluciones.total} bg="#ffedd5" color="#c2410c" />
            <KpiCard label="Casos devueltos al menos 1 vez" value={kpisDevoluciones.casos} bg="#fef2f2" color="#991b1b" />
          </div>

          {/* Tabla dinámica Aliado → Ciudad → Nodo: cada fila es un filtro
              para la tabla de detalle de abajo. */}
          <TablaPivoteDevoluciones
            datos={resumenAliadoCiudadNodo}
            filtroAliado={filtroAliadoDevol}
            filtroCiudad={filtroCiudadDevol}
            filtroNodo={filtroNodoDevol}
            onFiltrar={filtrarPivoteDevol}
            onLimpiar={limpiarPivoteDevol}
          />

          {/* Tabla de detalle: los casos que coinciden con lo filtrado arriba. */}
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px 0", fontSize: "13.5px", fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "8px" }}>
              Detalle de casos
              {(filtroAliadoDevol || filtroCiudadDevol || filtroNodoDevol) && (
                <span style={{ fontSize: "11.5px", fontWeight: "500", color: CLARO_MUTED }}>
                  — filtrando por {[filtroAliadoDevol, filtroCiudadDevol, filtroNodoDevol].filter(Boolean).join(" / ")}
                  {" "}
                  <button onClick={limpiarPivoteDevol} style={{ border: "none", background: "none", color: CLARO_RED, fontWeight: "700", cursor: "pointer", fontSize: "11.5px" }}>Quitar ✕</button>
                </span>
              )}
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
                <thead>
                  <tr>
                    <Th>Nodo</Th><Th>Tipo</Th><Th>Aliado</Th><Th>Ciudad</Th>
                    <Th>TAS</Th><Th>OT</Th><Th>CHG</Th>
                    <Th>Estado</Th><Th>Devoluciones</Th><Th>Última observación</Th><Th>Acciones</Th>
                  </tr>
                </thead>
                <tbody>
                  {filasDetalleDevol.map((f) => {
                    const info = ESTADO_INFO[f.estado] || ESTADO_INFO["En Revision"];
                    return (
                      <tr key={f.id}>
                        <Td style={{ fontWeight: "700" }}>{f.nodo}</Td>
                        <Td>{f.tipo_solicitud || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Sin tipo</span>}</Td>
                        <Td>{f.aliado}</Td>
                        <Td>{f.ciudad}</Td>
                        <Td>{f.tas || "—"}</Td>
                        <Td>{f.ot || "—"}</Td>
                        <Td>{f.chg || "—"}</Td>
                        <Td><Tag text={info.label} bg={info.bg} color={info.color} /></Td>
                        <Td>{f.veces_devuelto > 0 ? <Tag text={`${f.veces_devuelto}×`} bg="#ffedd5" color="#c2410c" /> : <span style={{ color: "#cbd5e1" }}>—</span>}</Td>
                        <Td style={{ maxWidth: "240px" }}>
                          {f.ultima_nota ? (
                            <span title={f.ultima_nota} style={{
                              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                              overflow: "hidden", fontSize: "12px", color: CLARO_DARK, lineHeight: 1.35,
                            }}>{f.ultima_nota}</span>
                          ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                        </Td>
                        <Td>
                          <button onClick={() => abrirHistorial(f)} style={{ border: "none", background: "none", color: CLARO_RED, fontWeight: "700", cursor: "pointer", padding: "5px 2px", fontSize: "12px" }}>Historial →</button>
                        </Td>
                      </tr>
                    );
                  })}
                  {filasDetalleDevol.length === 0 && (
                    <tr><td colSpan={11} style={{ padding: "20px", textAlign: "center", color: CLARO_MUTED, fontSize: "13px" }}>Sin casos para este filtro.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══════ Modal: crear/editar ══════ */}
      {formAbierto && (
        <div onClick={cerrarForm} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "24px" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: "16px", padding: "28px", width: "640px", maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "22px" }}>
              <div style={{ fontSize: "18px", fontWeight: "800", color: CLARO_DARK }}>
                {formAbierto === "nuevo" ? "Nuevo escalamiento" : `Editar escalamiento — ${formAbierto.nodo}`}
              </div>
              <button onClick={cerrarForm} style={{ border: "none", background: "#f1f5f9", borderRadius: "8px", width: "28px", height: "28px", cursor: "pointer", fontSize: "14px", color: CLARO_MUTED }}>✕</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {/* ── Bloque: a qué caso corresponde ── */}
              <div>
                <label style={labelSt}>Nodo</label>
                <SelectorNodo nodos={nodos} destacados={esCertificacionHuella ? nodosPendientes : []} valor={formDatos.nodo} disabled={formAbierto !== "nuevo"}
                  onSeleccionar={(id) => setFormDatos((p) => ({ ...p, nodo: id }))} />
                {formAbierto !== "nuevo" && <div style={{ fontSize: "10.5px", color: CLARO_MUTED, marginTop: "5px" }}>El nodo no se puede cambiar después de creado el caso.</div>}
                {formAbierto === "nuevo" && esCertificacionHuella && nodosPendientes.length > 0 && (
                  <div style={{ fontSize: "10.5px", color: "#075985", marginTop: "5px" }}>
                    {nodosPendientes.length} nodo(s) priorizado(s) pendiente(s) de certificar — aparecen primero en la lista.
                  </div>
                )}
              </div>

              <div>
                <label style={labelSt}>Tipo de solicitud</label>
                <div style={{ display: "flex", gap: "10px" }}>
                  {TIPOS_SOLICITUD.map((t) => {
                    const activo = formDatos.tipo_solicitud === t;
                    return (
                      <button key={t} type="button" onClick={() => setFormDatos((p) => ({ ...p, tipo_solicitud: t }))}
                        style={{
                          flex: 1, textAlign: "left", cursor: "pointer", borderRadius: "10px", padding: "12px 14px",
                          border: `2px solid ${activo ? CLARO_RED : BORDER}`, background: activo ? "#fef2f2" : "white",
                        }}>
                        <div style={{ fontSize: "13px", fontWeight: "700", color: activo ? CLARO_RED : CLARO_DARK }}>
                          {t === "Creación TAS" ? "🆕 " : "📡 "}{t}
                        </div>
                        <div style={{ fontSize: "10.5px", color: CLARO_MUTED, marginTop: "3px" }}>
                          {t === "Creación TAS" ? "Gestión propia, sin pasar por el NOC" : "Pasa por el NOC hasta quedar certificado"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label style={labelSt}>Fecha de creación</label>
                <input type="datetime-local" value={formDatos.fecha_escalamiento || ""} disabled
                  style={{ ...inputSt, width: "100%", background: "#f1f5f9", color: CLARO_MUTED, cursor: "not-allowed" }} />
                <div style={{ fontSize: "10.5px", color: CLARO_MUTED, marginTop: "5px" }}>Se registra sola, con la hora real al guardar — no se puede editar.</div>
              </div>

              {/* ── Panel: identificadores del caso (TAS / OT / CHG) ── */}
              <div style={{ background: "#f8fafc", border: `1px solid ${BORDER}`, borderRadius: "12px", padding: "16px" }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "12px" }}>
                  Identificadores del caso
                </div>
                <div style={{ display: "flex", gap: "16px" }}>
                  <CampoIdentificador icono="🎫" label="TAS" valor={formDatos.tas} onChange={(e) => setFormDatos((p) => ({ ...p, tas: e.target.value }))} requerido={esCertificacionHuella} />
                  <CampoIdentificador icono="🧾" label="OT"  valor={formDatos.ot}  onChange={(e) => setFormDatos((p) => ({ ...p, ot: e.target.value }))} requerido={esCertificacionHuella} />
                  <CampoIdentificador icono="🔄" label="CHG" valor={formDatos.chg} onChange={(e) => setFormDatos((p) => ({ ...p, chg: e.target.value }))} requerido={esCertificacionHuella} />
                </div>
              </div>

              <div>
                <label style={labelSt}>Notas (opcional)</label>
                <textarea value={formDatos.descripcion || ""} onChange={(e) => setFormDatos((p) => ({ ...p, descripcion: e.target.value }))}
                  rows={3} placeholder="Cualquier detalle adicional del caso…" style={{ ...inputSt, width: "100%", resize: "vertical", fontFamily: "inherit" }} />
              </div>

              {errorForm && <div style={{ color: "#991b1b", fontSize: "12.5px", fontWeight: "600" }}>⚠️ {errorForm}</div>}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "4px" }}>
                <button onClick={cerrarForm} style={{ border: `1px solid ${BORDER}`, background: "white", color: CLARO_DARK, borderRadius: "8px", padding: "9px 16px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>Cancelar</button>
                <button onClick={guardarForm} disabled={guardandoForm} style={{
                  border: "none", borderRadius: "8px", padding: "9px 18px", cursor: guardandoForm ? "default" : "pointer",
                  background: guardandoForm ? "#9ca3af" : `linear-gradient(135deg, ${CLARO_RED}, #B0000E)`, color: "white", fontSize: "13px", fontWeight: "700",
                }}>{guardandoForm ? "Guardando…" : "Guardar"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════ Modal: historial ══════ */}
      {historialAbierto && (
        <div onClick={() => setHistorialAbierto(null)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "24px" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: "16px", padding: "28px", width: "680px", maxWidth: "96vw", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "18px" }}>
              <div>
                <div style={{ fontSize: "11px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase" }}>Historial del escalamiento</div>
                <div style={{ fontSize: "20px", fontWeight: "800", color: CLARO_DARK, letterSpacing: "-0.01em" }}>{historialAbierto.nodo} — {historialAbierto.tipo_solicitud || "Sin tipo"}</div>
              </div>
              <button onClick={() => setHistorialAbierto(null)} style={{ border: "none", background: "#f1f5f9", borderRadius: "8px", width: "28px", height: "28px", cursor: "pointer", fontSize: "14px", color: CLARO_MUTED }}>✕</button>
            </div>
            <LineaTiempoEscalamiento eventos={historialDatos} cargando={historialCargando} />
          </div>
        </div>
      )}

      {/* ══════ Modal: observación del cambio de estado ══════ */}
      {cambioPendiente && (() => {
        const textos = TEXTOS_MODAL_CAMBIO_ESTADO[estadoPendiente] || { titulo: "Observación", placeholder: "Detalle del cambio de estado (opcional)" };
        const esDevolucion = estadoPendiente === "Devuelto";
        return (
          <div onClick={() => setCambioPendiente(null)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "24px" }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: "16px", padding: "28px", width: "480px", maxWidth: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
              <div style={{ fontSize: "18px", fontWeight: "800", color: CLARO_DARK, marginBottom: "6px" }}>
                {textos.titulo}
              </div>
              <div style={{ fontSize: "12.5px", color: CLARO_MUTED, marginBottom: "16px" }}>
                Nodo <strong>{cambioPendiente.nodo}</strong> — {ESTADO_INFO[cambioPendiente.estado]?.label || cambioPendiente.estado} → <strong>{ESTADO_INFO[estadoPendiente]?.label || estadoPendiente}</strong>. Esta observación queda guardada en el historial.
              </div>
              <textarea value={notaCambio} onChange={(e) => setNotaCambio(e.target.value)}
                rows={4} autoFocus placeholder={textos.placeholder}
                style={{ ...inputSt, width: "100%", resize: "vertical", fontFamily: "inherit" }} />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px" }}>
                <button onClick={() => setCambioPendiente(null)} style={{ border: `1px solid ${BORDER}`, background: "white", color: CLARO_DARK, borderRadius: "8px", padding: "9px 16px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>Cancelar</button>
                <button onClick={confirmarCambioEstado} style={{
                  border: "none", borderRadius: "8px", padding: "9px 18px", cursor: "pointer",
                  background: `linear-gradient(135deg, ${CLARO_RED}, #B0000E)`, color: "white", fontSize: "13px", fontWeight: "700",
                }}>{esDevolucion ? "Confirmar devolución" : "Confirmar cambio"}</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
